"use client";

import { useState } from "react";
import Link from "next/link";
import {
  Clock3,
  UserRound,
  UsersRound,
  BarChart3,
  CheckCircle2,
  Settings2,
  TrendingUp,
  Target,
  ArrowRight,
} from "lucide-react";

const engagementModels = [
  {
    id: "hourly-retainer",
    title: "Hourly Retainer",
    subtitle: "Flexible accounting support with monthly availability",
    icon: Clock3,
    howItWorks:
      "The Hourly Retainer model gives your business access to accounting and finance support through a committed pool of hours. It is ideal for companies that need recurring help without requiring a dedicated full-time or fractional resource.",
    marginImpact:
      "This model reduces the cost of idle internal capacity. Instead of hiring full-time for fluctuating workloads, businesses use support only when needed. AI-enabled workflows also improve speed across reconciliations, review, and reporting preparation.",
    bestFit:
      "Best for businesses with variable accounting needs, seasonal workloads, month-end pressure, reconciliation backlogs, or teams that need flexible support without increasing fixed headcount.",
    idealIndustryTypes: [
      "Professional Services",
      "Consulting Firms",
      "Small Businesses",
      "Seasonal Businesses",
      "Startups",
      "Founder-Led Companies",
    ],
    clientProfiles: [
      "Variable Monthly Workload",
      "Month-End Support Needed",
      "Reconciliation Backlogs",
      "No Full-Time Hiring Need",
      "Flexible Finance Support",
      "Cost-Conscious Teams",
    ],
    outcomes: ["Lower Overhead", "Faster Turnaround", "Better Visibility"],
  },

  {
    id: "dedicated-fte",
    title: "Dedicated FTE",
    subtitle: "A full-time accounting resource in your workflow",
    icon: UserRound,
    howItWorks:
      "The Dedicated FTE model provides a full-time accounting or finance resource who works as an extension of your internal team. The resource is trained on your systems, reporting formats, accounting policies, and recurring workflows.",
    marginImpact:
      "This model reduces the cost of scaling the finance function. Instead of adding internal payroll, benefits, and management overhead, the business gains dedicated execution capacity in a more efficient structure.",
    bestFit:
      "Best for businesses with sustained accounting volume, recurring reporting needs, multi-entity activity, or a need for long-term workflow ownership.",
    idealIndustryTypes: [
      "Multi-Location Businesses",
      "E-commerce Companies",
      "Healthcare Operators",
      "Real Estate Businesses",
      "Manufacturing Companies",
      "Growing Service Companies",
    ],
    clientProfiles: [
      "Sustained Accounting Volume",
      "Long-Term Process Ownership",
      "Multi-Entity Accounting",
      "Recurring Reporting Needs",
      "Internal Team Extension",
      "High Transaction Activity",
    ],
    outcomes: [
      "Execution Consistency",
      "Scalable Capacity",
      "Operational Stability",
    ],
  },

  {
    id: "fractional-fte",
    title: "Fractional FTE",
    subtitle: "Recurring finance capacity without full-time hiring",
    icon: UsersRound,
    howItWorks:
      "The Fractional FTE model gives your business structured monthly access to accounting and finance support without committing to a full-time resource. It supports continuity across reporting, analysis, and recurring finance workflows.",
    marginImpact:
      "This model improves finance team leverage by giving businesses access to experienced support at a fraction of the cost of full-time hiring. AI-enabled workflows also reduce preparation time across bookkeeping, reporting, and analysis.",
    bestFit:
      "Best for businesses that need recurring reporting, budgeting, forecasting, KPI tracking, or finance support beyond basic bookkeeping.",
    idealIndustryTypes: [
      "Growth-Stage Companies",
      "Venture-Backed Startups",
      "Private Equity Portfolio Companies",
      "Agencies & Service Firms",
      "SaaS Businesses",
      "Founder-Led Businesses",
    ],
    clientProfiles: [
      "Needs Regular Finance Support",
      "Not Ready for Full-Time Hire",
      "Budgeting & Forecasting Needs",
      "KPI Reporting Required",
      "Management Reporting Needed",
      "Finance Team Leverage",
    ],
    outcomes: ["Higher Team Leverage", "Smarter Reporting", "Margin Awareness"],
  },

  {
    id: "volume-based-monthly-retainer",
    title: "Volume-Based Monthly Retainer",
    subtitle: "Predictable support for recurring transaction volume",
    icon: BarChart3,
    howItWorks:
      "This model is designed for businesses with recurring transaction activity and relatively simple accounting needs. It is structured around transaction volume and routine ledger maintenance, with broader accounting support added later as needed.",
    marginImpact:
      "This model reduces the time founders, operators, and admin teams spend recording transactions and maintaining accounting records. AI-enabled categorization and review workflows improve accuracy while reducing manual effort.",
    bestFit:
      "Best for startups, small businesses, and early-stage companies that need recurring transaction recording, inventory bookkeeping, and basic ledger maintenance.",
    idealIndustryTypes: [
      "Startups",
      "Small Businesses",
      "Retail Businesses",
      "E-commerce Stores",
      "Inventory-Based Businesses",
      "Early-Stage Companies",
    ],
    clientProfiles: [
      "Recurring Transactions",
      "Basic Bookkeeping Needs",
      "Ledger Maintenance Required",
      "Predictable Monthly Volume",
      "Clean Books Needed",
      "Low Internal Finance Capacity",
    ],
    outcomes: [
      "Cleaner Books",
      "Lower Admin Burden",
      "Predictable Support Model",
    ],
  },
];

export default function AccountingEngagementModels() {
  const [activeId, setActiveId] = useState(engagementModels[0].id);

  const activeModel =
    engagementModels.find((model) => model.id === activeId) || engagementModels[0];

  return (
    <section className="bg-white py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-6">
        {/* Heading */}
        <div className="mx-auto max-w-5xl text-center">
          <div className="mb-4 inline-flex items-center gap-3 text-xs md:text-sm font-semibold uppercase tracking-[0.22em] text-[#2563EB]">
            <span className="h-px w-8 md:w-12 bg-[#93C5FD]" />
            AI-Enabled Accounting Engagement Models
            <span className="h-px w-8 md:w-12 bg-[#93C5FD]" />
          </div>

          <h2 className="text-balance text-3xl font-extrabold leading-tight text-[#0B1641] sm:text-4xl lg:text-5xl">
            Scalable Accounting Support Built Around How{" "}
            <span className="text-[#2563EB]">Your Business Works</span>
          </h2>

          <p className="mx-auto mt-6 max-w-4xl text-base md:text-lg leading-8 text-slate-600">
            We provide flexible accounting engagement models designed for growing
            businesses that need reliable execution, faster turnaround, and better
            financial visibility without increasing internal finance headcount. Our
            AI-enabled workflows reduce repetitive manual effort, improve review
            efficiency, and help businesses protect margins as transaction volume
            and reporting needs grow.
          </p>
        </div>

        {/* Top Cards */}
        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {engagementModels.map((model) => {
            const Icon = model.icon;
            const isActive = model.id === activeId;

            return (
              <button
                key={model.id}
                type="button"
                onClick={() => setActiveId(model.id)}
                className={`group relative mx-auto w-full max-w-[260px] rounded-[18px] border bg-white p-4 text-left transition-all duration-300 ${
                  isActive
                    ? "border-[#2563EB] shadow-[0_16px_36px_rgba(37,99,235,0.10)]"
                    : "border-slate-200 shadow-sm hover:border-[#93C5FD] hover:shadow-[0_12px_28px_rgba(15,23,42,0.06)]"
                }`}
              >
                {isActive && (
                  <div className="absolute right-4 top-4">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2563EB] text-white">
                      <CheckCircle2 className="h-5 w-5" />
                    </div>
                  </div>
                )}

                <div className="flex flex-col items-center justify-center text-center gap-2">
                  <div
                    className={`flex h-10 w-10 md:h-12 md:w-12 shrink-0 items-center justify-center rounded-full ${
                      isActive ? "bg-[#2563EB]" : "bg-slate-100"
                    }`}
                  >
                    <Icon
                      className={`h-5 w-5 md:h-6 md:w-6 ${
                        isActive ? "text-white" : "text-[#2563EB]"
                      }`}
                    />
                  </div>

                  <div className="min-w-0 min-h-[48px] md:min-h-[56px] flex items-center justify-center">
                    <h3 className="text-base md:text-lg font-medium tracking-tight leading-snug text-[#0B1641]">
                      {model.title}
                    </h3>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Detail Panel */}
        <div className="mt-10 rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_18px_40px_rgba(15,23,42,0.06)] md:p-8 xl:p-10">
          <div className="grid grid-cols-1 gap-10 xl:grid-cols-[1.35fr_0.95fr] xl:gap-12">
            {/* Left */}
            <div className="xl:border-r xl:border-slate-200 xl:pr-10">
              <div className="mb-6 inline-flex rounded-full bg-[#EFF6FF] px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-[#2563EB]">
                {activeModel.subtitle}
              </div>

              <h3 className="text-2xl sm:text-3xl md:text-[34px] font-bold tracking-tight leading-tight text-[#0B1641]">
                {activeModel.title}
              </h3>

              <div className="mt-8 space-y-8">
                {[
                  {
                    title: "How It Works",
                    body: activeModel.howItWorks,
                    icon: Settings2,
                  },
                  {
                    title: "Margin Impact",
                    body: activeModel.marginImpact,
                    icon: TrendingUp,
                  },
                  {
                    title: "Best-Fit Use Case",
                    body: activeModel.bestFit,
                    icon: Target,
                  },
                ].map((item, index) => {
                  const ItemIcon = item.icon;

                  return (
                    <div key={index} className="flex items-start gap-5">
                      <div className="mt-1 flex h-14 w-14 md:h-16 md:w-16 shrink-0 items-center justify-center rounded-2xl bg-[#F8FAFC] ring-1 ring-slate-200">
                        <ItemIcon className="h-7 w-7 md:h-8 md:w-8 text-[#2563EB]" />
                      </div>

                      <div>
                        <h4 className="text-lg md:text-[24px] font-semibold leading-snug text-[#0B1641]">
                          {item.title}
                        </h4>
                        <p className="mt-2 text-sm md:text-[15px] leading-7 text-slate-700">
                          {item.body}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-10">
                <Link
                  href="/contact"
                  className="inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-2xl bg-[#2563EB] px-7 py-3.5 text-sm md:text-base font-semibold text-white shadow-[0_16px_34px_rgba(37,99,235,0.24)] transition hover:bg-[#1D4ED8]"
                >
                  Request Consultation
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </div>
            </div>

            {/* Right */}
            <div className="space-y-6">
              <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm">
                <h5 className="text-base md:text-lg font-semibold text-[#0B1641]">
                  Ideal Industry Types
                </h5>
                <div className="mt-4 flex flex-wrap gap-3">
                  {activeModel.idealIndustryTypes.map((item, index) => (
                    <span
                      key={index}
                      className="rounded-full border border-[#BFDBFE] bg-[#F8FBFF] px-3.5 py-1.5 text-xs md:text-sm font-medium text-[#1D4ED8]"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm">
                <h5 className="text-base md:text-lg font-semibold text-[#0B1641]">
                  Business Outcomes
                </h5>
                <div className="mt-4 flex flex-wrap gap-3">
                  {activeModel.outcomes.map((item, index) => (
                    <span
                      key={index}
                      className="rounded-full border border-[#D1FAE5] bg-[#F0FDF4] px-3.5 py-1.5 text-xs md:text-sm font-medium text-[#047857]"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm">
                <h5 className="text-base md:text-lg font-semibold text-[#0B1641]">
                  Client Profile
                </h5>
                <div className="mt-4 flex flex-wrap gap-3">
                  {activeModel.clientProfiles.map((item, index) => (
                    <span
                      key={index}
                      className="rounded-full border border-slate-200 bg-slate-50 px-3.5 py-1.5 text-xs md:text-sm font-medium text-slate-700"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 flex items-center justify-center gap-3 border-t border-slate-200 pt-6 text-sm font-medium text-slate-500">
            <CheckCircle2 className="h-4 w-4 text-slate-500" />
            All engagement models are AI-enabled, secure, and designed to scale with your business.
          </div>
        </div>
      </div>
    </section>
  );
}